<template>
  <div class="consumer-cart">
    <h2>장바구니</h2>
    <div class="main-container" v-if="cartDatas.length > 0">
      <div class="product-list-container">
        <div v-for="(item, index) in productDatas" :key="index">
          <hr class="section" v-if="index !== 0" />
          <div class="desc-container">
            <img :src="item.productThumbnailUrl.originalUrl" />
            <div>
              <p class="name">{{ item.productName }}</p>
              <p class="sell-price">
                <span>계좌이체가</span>
                {{ item.productBankSellPrice.toLocaleString() }}원
              </p>
              <p class="sell-price">
                <span>카드결제가</span>
                {{ item.productSellPrice.toLocaleString() }}원
              </p>
            </div>
            <button type="button" @click="removeCart(index)">
              <span class="material-icons-outlined"> close </span>
            </button>
          </div>
          <div class="option-container">
            <p>
              └ [옵션:
              <span> {{ item.selectedOptionName }} </span>]
            </p>
          </div>
          <div class="count-container">
            <p>수량</p>
            <div>
              <input
                type="number"
                min="1"
                step="1"
                max="99"
                v-model.number="item.count"
                @blur="changeOptionCount(index)"
              />
            </div>
          </div>
          <hr />
          <div class="option-price-container">
            <p>주문금액</p>
            <strong>
              {{ (item.productBankSellPrice * item.count).toLocaleString()
              }}<span>원</span> ({{
                (item.productSellPrice * item.count).toLocaleString()
              }}<span>원</span>)
            </strong>
          </div>
        </div>
      </div>
      <div class="checkout-container">
        <div>
          <div>
            <p>총 상품금액</p>
            <p>
              {{ totalBankSellPrice.toLocaleString() }}<span>원</span><br />({{
                totalCardSellPrice.toLocaleString()
              }}<span>원</span>)
            </p>
          </div>
          <div>
            <p>총 배송비</p>
            <p>결제 시 확인</p>
          </div>
          <hr />
          <div>
            <strong>결제 예정금액</strong>
            <strong>
              {{ totalBankSellPrice.toLocaleString() }}<span>원</span><br />({{
                totalCardSellPrice.toLocaleString()
              }}<span>원</span>)
            </strong>
          </div>
        </div>
        <button type="button" @click="buyNow">상품 주문하기</button>
        <p>배송비는 배송 방법에 따라 상이하여 주문서에서 확인 가능합니다.</p>
      </div>
    </div>
    <div class="empty-container" v-else>
      <span class="material-icons-outlined"> error_outline </span>
      <p>장바구니가 비어있습니다.</p>
    </div>
  </div>
</template>

<script setup lang="js">
import { onMounted, ref, computed } from "vue";
import { db } from "@/lib/firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { useRouter } from "vue-router";
import { getUserId } from "@/lib/auth";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const cartDatas = ref([]);
const productDatas = ref([]);

const router = useRouter();

const totalBankSellPrice = computed(() => {
  return productDatas.value.reduce(
    (acc, curr) => acc + (Number(curr.productBankSellPrice || 0) * Number(curr.count || 0)),
    0
  );
});

const totalCardSellPrice = computed(() => {
  return productDatas.value.reduce(
    (acc, curr) => acc + (Number(curr.productSellPrice || 0) * Number(curr.count || 0)),
    0
  );
});

const buyNow = async () => {
  try {
    const itemsToBuy = cartDatas.value.map((selected) => ({
      productCode: selected.productCode,
      optionName: selected.optionName,
      count: Number(selected.count || 1),
    }));

    if (itemsToBuy.length === 0) {
      alert("구매할 옵션이 없습니다.");
      return;
    }

    router.push({
      path: "/checkout",
      state: {
        orderItems: itemsToBuy,
        query: "cart",
      },
    });
  } catch (error) {
    console.error("Error buying now:", error);
    alert("구매 실패하였습니다. 다시 시도해주세요.");
  }
};

const updateCartData = async () => {
  const uid = getUserId();
  await updateDoc(doc(db, "users", uid), {
    userProductCartList: cartDatas.value,
  });
};

const changeOptionCount = (index) =>
  runLocked(async () => {
    const updatedItem = productDatas.value[index];
    if (!updatedItem) return;

    let count = Number(updatedItem.count);

    if (!Number.isInteger(count) || count <= 0) {
      count = 1;
    }
    if (count > 99) {
      count = 99;
    }

    // 화면용 데이터 보정
    updatedItem.count = count;

    // 실제 장바구니 데이터 반영
    const cartItem = cartDatas.value.find(
      (c) =>
        c.productCode === updatedItem.productId &&
        c.productOptionCode === updatedItem.selectedOptionCode
    );

    if (cartItem) {
      cartItem.count = count;
    }

    await updateCartData();
    console.log("Cart updated for item:", updatedItem.productName);
  }).catch((error) => {
    console.error("Error updating cart:", error);
    alert("수량 변경 중 오류가 발생했습니다.");
  });

const removeCart = (index) =>
  runLocked(async () => {
    if (!confirm("해당 품목을 삭제하시겠습니까?")) return;

    const target = productDatas.value[index];
    if (!target) return;

    // cartDatas에서 동일 항목 제거
    cartDatas.value = cartDatas.value.filter(
      (c) =>
        !(
          c.productCode === target.productId &&
          c.productOptionCode === target.selectedOptionCode
        )
    );

    // 화면 데이터도 제거
    productDatas.value.splice(index, 1);

    await updateCartData();
    alert("해당 품목이 삭제되었습니다!");
  }).catch((error) => {
    console.error("Error removing cart item:", error);
    alert("장바구니 삭제 중 오류가 발생했습니다.");
  });

const fetchCartData = async () => {
  const uid = getUserId();
  const userSnap = await getDoc(doc(db, "users", uid));

  if (!userSnap.exists()) {
    cartDatas.value = [];
    return;
  }

  const userData = userSnap.data();
  cartDatas.value = Array.isArray(userData.userProductCartList)
    ? userData.userProductCartList
    : [];
};

const fetchProductData = async () => {
  if (!cartDatas.value.length) {
    productDatas.value = [];
    return;
  }

  // Promise.all로 병렬 조회 + 완료 대기
  const results = await Promise.all(
    cartDatas.value.map(async (cartItem) => {
      try {
        const productSnap = await getDoc(doc(db, "product", cartItem.productCode));
        if (!productSnap.exists()) return null;

        return {
          selectedOptionCode: cartItem.productOptionCode,
          selectedOptionName: cartItem.optionName,
          count: Number(cartItem.count || 1),
          productId: productSnap.id,
          ...productSnap.data(),
        };
      } catch (e) {
        console.error("상품 조회 실패:", cartItem.productCode, e);
        return null;
      }
    })
  );

  // null 제거
  productDatas.value = results.filter(Boolean);
};

onMounted(() => {
  runLocked(async () => {
    await fetchCartData();
    await fetchProductData();
  }).catch((error) => {
    console.error("Failed to fetch data:", error);
    alert("장바구니 정보를 불러오지 못했습니다.");
  });
});
</script>

<style lang="scss" scoped>
.consumer-cart {
  padding: 16px 24px;

  > h2 {
    text-align: center;
  }

  > .main-container {
    margin-top: 36px;

    > .product-list-container {
      flex: 1;

      > div {
        > hr {
          margin: 16px 0;
          border: none;
          border-top: 1px solid #efefef;
          &.section {
            margin: 24px 0;
            border-top: 1px solid #999;
          }
        }
        > .desc-container {
          display: flex;
          gap: 16px;

          > img {
            width: 96px;
            height: 96px;
            border-radius: 8px;
            object-fit: cover;
            border: 1px solid #efefef;
            padding: 4px;
          }

          > div {
            flex: 1;

            > .name {
              font-weight: 700;
              font-size: 16px;
              margin-bottom: 8px;
            }

            > .sell-price {
              line-height: 24px;
              font-size: 14px;

              > span {
                font-weight: 700;
                font-size: 14px;
                color: #007bff;
                margin-right: 4px;
              }
            }
          }

          > button {
            width: 24px;
            height: 24px;
            border: none;
            background: none;
            font-size: 18px;
            cursor: pointer;
          }
        }

        > .option-container {
          background: #efefef;
          margin-top: 16px;
          padding: 12px;

          > p {
            font-size: 14px;
            > span {
              font-weight: 700;
              color: #007bff;
            }
          }
        }

        > .count-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 16px;

          > div {
            display: flex;
            align-items: center;
            gap: 8px;
            > input {
              width: 64px;
              padding: 8px;
              font-size: 14px;
              text-align: center;
              border: 1px solid #999;
            }
          }
        }

        > .option-price-container {
          display: flex;
          align-items: center;
          justify-content: space-between;

          > strong {
            > span {
              font-weight: 400;
              margin-left: 2px;
            }
          }
        }
      }
    }

    > .checkout-container {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 48px;
      > div {
        border: 1px solid black;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;

        > div {
          display: flex;
          align-items: center;
          justify-content: space-between;

          > p,
          strong {
            font-size: 16px;
            text-align: end;
            line-height: 24px;
            > span {
              margin-left: 4px;
              font-size: 14px;
            }
          }
        }
      }

      > button {
        border: none;
        background: black;
        color: white;
        font-weight: 700;
        font-size: 18px;
        height: 48px;
        border-radius: 4px;
        cursor: pointer;
      }

      > p {
        font-size: 14px;
        color: #666;
        text-align: center;
        word-break: keep-all;
      }
    }
  }

  > .empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    height: 480px;
    border-bottom: 1px solid #efefef;

    > span {
      font-size: 96px;
      color: #999;
    }

    > p {
      color: #999;
      font-size: 18px;
      font-weight: 700;
    }
  }
}
</style>
